import { useCreateListingMutation, useTradingListingsQuery, useInstantSellMutation } from '@http';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';
import { useState } from 'react';

interface SellItemProps {
  name: string;
  onClose?: () => void;
};

export function SellItem({ name, onClose }: SellItemProps) {
  const [amount, setAmount] = useState(1);
  const [price, setPrice] = useState(0);

  const { t } = useTranslation(undefined, { keyPrefix: 'PlayerMarket.Sell' });  
  const { t: tItems } = useTranslation(undefined, { keyPrefix: 'Items' });
  const { mutateAsync } = useCreateListingMutation();
  const { mutateAsync: sellInstantAsync } = useInstantSellMutation();
  const { data: listings } = useTradingListingsQuery();
  const { id, userData, isFetching, setIsFetching, refetch } = useAppStore();

  const maxAmount = userData.items.find(i => i.key === name)?.amount || 0;

  const add = () => setAmount(Math.min(amount + 1, maxAmount));
  const sub = () => setAmount(Math.max(amount - 1, 1));

  const recommendedPrice = (listings ?? [])
    .filter(l => l.key === name)
    .sort((a, b) => a.price - b.price)[0]?.price ?? 0;

  const sell = async () => {
    if (isFetching) return;
    setIsFetching(true);

    await mutateAsync({ key: name, amount, price });

    onClose?.();
    refetch();

    toast(t('success'), { type: 'success' });
  };

  const instantSell = async () => {
    if (isFetching) return;
    setIsFetching(true);

    await sellInstantAsync({ key: name, amount });

    onClose?.();
    refetch();

    toast(t('instantSuccess'), { type: 'success' });
  };

  const total = Number.isFinite(amount * price) ? amount * price : 0;
  const ownListings = (listings ?? []).filter(l => l.user.id === id).length;
  const reachedLimit = userData.premiumUntil && new Date(userData.premiumUntil) >= new Date()
    ? ownListings >= 10
    : ownListings >= 5;

  const hasPro = !!userData.premiumUntil && new Date(userData.premiumUntil) >= new Date();
  const instantSellPrice = Shared.InstantSellCalculator.calc(name, hasPro) * amount;

  const canSell = amount > 0 && amount <= maxAmount && price > 0 && !reachedLimit;
  const canInstantSell = amount > 0 && amount <= maxAmount;

  return (
    <div className="p-2">
      <p>{tItems(name)}</p>

      <div className="flex w-full items-center justify-between my-4">
        <button className="mod-decrease-level" onClick={sub}>
          <IconMinus />
        </button>

        <div className="relative mx-4 w-full">
          <input
            onChange={e => setAmount(parseInt(e.target.value))}
            className="input border border-secondary"
            value={amount}
            type="number"
          />

          <p className={`${amount > maxAmount && 'text-red-500'} absolute bottom-0.25 right-1 text-xs text-gray-400`}>
            {maxAmount}
          </p>
        </div>

        <button className="mod-increase-level" onClick={add}>
          <IconPlus />
        </button>
      </div>

      <label className="text-sm">{t('price')}</label>
      <input
        onChange={e => setPrice(parseInt(e.target.value))}
        className="input border border-secondary"
        type="number"
        value={price}
      />

      <p className="text-lime-500 text-sm mt-1">
        {t('total', { total, recommendedPrice })}
      </p>

      <p className="text-sm mt-2">
        {t('removalInfo')}
      </p>

      {reachedLimit && (
        <p className="text-red-500 text-sm mt-2">
          {t('limit')}
        </p>
      )}

      <Button disabled={!canSell} className="mt-6" onClick={sell}>
        {t('sell')}
      </Button>

      <div className="flex items-center mt-3">
        <div className="border-b border-secondary flex-1" />
        <p className="text-xs text-gray-400 mx-2">
          {t('or')}
        </p>
        <div className="border-b border-secondary flex-1" />
      </div>

      <Button disabled={!canInstantSell} onClick={instantSell} className="mt-3">
        {t('instantSell', { price: instantSellPrice })}
      </Button>

      {!hasPro && (
        <p className="text-xs text-center slight-active mt-1">
          {t('instantSellPromo')}
        </p>
      )}
    </div>
  );
}